import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatINR, uploadFileUrl } from "@/lib/api";
import { Loader2, Plus, Pencil, Trash2, X, Upload, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyProduct = {
    slug: "", name: "", tagline: "", description: "", category_slug: "single", category_id: "",
    images: [], base_price_inr: 1499, sizes: [], options: [], material: "Premium Polyresin",
    is_active: true, is_featured: false, requires_photos: true, cod_available: false,
};

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileRef = useRef();

    const uploadImages = async (files) => {
        if (!files || !files.length) return;
        setUploadingImage(true);
        try {
            const uploaded = [];
            for (const file of files) {
                const form = new FormData();
                form.append("file", file);
                form.append("kind", "product_image");
                const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(uploadFileUrl(data.path));
            }
            const current = (editing?.images || []).filter(Boolean);
            const next = [...current, ...uploaded];
            setEditing({ ...editing, images: next, images_str: next.join(",") });
            toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
        } catch (e) {
            toast.error("Upload failed");
        } finally {
            setUploadingImage(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeImage = (idx) => {
        const next = (editing.images || []).filter((_, i) => i !== idx);
        setEditing({ ...editing, images: next, images_str: next.join(",") });
    };

    const moveImage = (idx, dir) => {
        const arr = [...(editing.images || [])];
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        setEditing({ ...editing, images: arr, images_str: arr.join(",") });
    };

    const load = async () => {
        setLoading(true);
        try {
            const [p, c] = await Promise.all([api.get("/products", { params: { limit: 200, include_inactive: true } }), api.get("/categories")]);
            setProducts(p.data.products || []);
            setCategories(c.data.categories || []);
        } catch (e) {
            toast.error("Failed to load products");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const startEdit = (p) => { setEditing({ ...p, options: p.options || [], images_str: (p.images || []).join(",") }); setOpen(true); };
    const startCreate = () => { setEditing({ ...emptyProduct, images_str: "", sizes: [
        { label: "4 Inch", inches: 4, price_inr: 1499, weight_grams: 120, delivery_days: 12, active: true },
        { label: "7 Inch", inches: 7, price_inr: 2799, weight_grams: 260, delivery_days: 15, active: true },
    ] }); setOpen(true); };

    const save = async () => {
        if (!editing) return;
        const combined = [...(editing.images || [])];
        (editing.images_str || "").split(",").map((s) => s.trim()).filter(Boolean).forEach((url) => {
            if (!combined.includes(url)) combined.push(url);
        });
        const payload = {
            ...editing,
            images: combined,
            base_price_inr: Number(editing.base_price_inr),
        };
        delete payload.images_str;
        try {
            if (editing.id) {
                await api.patch(`/products/${editing.id}`, payload);
                toast.success("Updated");
            } else {
                const cat = categories.find((c) => c.slug === payload.category_slug);
                if (cat) payload.category_id = cat.id;
                await api.post("/products", payload);
                toast.success("Created");
            }
            setOpen(false); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const del = async (p) => {
        if (!confirm(`Delete "${p.name}"?`)) return;
        await api.delete(`/products/${p.id}`); toast.success("Deleted"); load();
    };

    return (
        <div className="p-8" data-testid="admin-products">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Products</h1>
                    <p className="text-sm text-muted-foreground">Manage the miniature catalogue.</p>
                </div>
                <Button onClick={startCreate} data-testid="admin-add-product" className="rounded-full bg-primary hover:bg-primary/90"><Plus className="w-4 h-4" /> Add Product</Button>
            </div>

            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : (
                <div className="rounded-2xl bg-card border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left">
                            <tr>
                                <th className="p-3">Product</th>
                                <th className="p-3">Category</th>
                                <th className="p-3">Base Price</th>
                                <th className="p-3">Sizes</th>
                                <th className="p-3">Status</th>
                                <th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id} className="border-t border-border">
                                    <td className="p-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-brand-cream overflow-hidden shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                                            <div>
                                                <div className="font-semibold">{p.name}</div>
                                                <div className="text-xs text-muted-foreground">{p.slug}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3">{p.category_slug}</td>
                                    <td className="p-3">{formatINR(p.base_price_inr)}</td>
                                    <td className="p-3">{p.sizes?.length || 0}</td>
                                    <td className="p-3">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{p.is_active ? "Active" : "Hidden"}</span>
                                        {p.is_featured && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-brand-gold text-primary-foreground">Featured</span>}
                                    </td>
                                    <td className="p-3 text-right">
                                        <button onClick={() => startEdit(p)} className="w-8 h-8 rounded-lg hover:bg-primary/5 text-primary"><Pencil className="w-4 h-4 mx-auto" /></button>
                                        <button onClick={() => del(p)} className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4 mx-auto" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl rounded-3xl">
                    <DialogHeader><DialogTitle>{editing?.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                            <div className="grid grid-cols-2 gap-2">
                                <Field label="Name"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
                                <Field label="Slug"><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></Field>
                            </div>
                            <Field label="Tagline"><Input value={editing.tagline || ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} /></Field>
                            <Field label="Description"><textarea className="w-full rounded-xl border p-2 text-sm min-h-[80px]" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
                            <div className="grid grid-cols-3 gap-2">
                                <Field label="Category">
                                    <select value={editing.category_slug} onChange={(e) => setEditing({ ...editing, category_slug: e.target.value })} className="w-full rounded-xl border h-10 px-2 text-sm">
                                        {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                                    </select>
                                </Field>
                                <Field label="Base price ₹"><Input type="number" value={editing.base_price_inr} onChange={(e) => setEditing({ ...editing, base_price_inr: e.target.value })} /></Field>
                                <Field label="Material"><Input value={editing.material} onChange={(e) => setEditing({ ...editing, material: e.target.value })} /></Field>
                            </div>
                            <Field label="Image URLs (comma separated)"><Input value={editing.images_str} onChange={(e) => setEditing({ ...editing, images_str: e.target.value })} placeholder="Paste image URLs, or use Upload below" /></Field>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Product images ({(editing.images || []).length})</div>
                                    <div className="flex gap-2">
                                        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImages(Array.from(e.target.files || []))} />
                                        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadingImage} data-testid="product-upload-image" className="rounded-full h-9">
                                            {uploadingImage ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Upload images</>}
                                        </Button>
                                    </div>
                                </div>
                                {(editing.images || []).length === 0 ? (
                                    <div className="rounded-xl border-2 border-dashed border-border p-8 text-center text-xs text-muted-foreground">
                                        Upload photos or paste URLs above. The first image becomes the product cover.
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-4 gap-2">
                                        {(editing.images || []).map((url, idx) => (
                                            <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border group">
                                                <img src={url} alt="" className="w-full h-full object-cover" />
                                                {idx === 0 && <span className="absolute top-1 left-1 text-[10px] font-bold uppercase tracking-widest bg-primary text-primary-foreground px-1.5 py-0.5 rounded">Cover</span>}
                                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/50 transition flex items-center justify-center gap-1">
                                                    <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} className="w-7 h-7 rounded-full bg-white text-primary text-xs font-bold disabled:opacity-30">‹</button>
                                                    <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === (editing.images || []).length - 1} className="w-7 h-7 rounded-full bg-white text-primary text-xs font-bold disabled:opacity-30">›</button>
                                                    <button type="button" onClick={() => removeImage(idx)} className="w-7 h-7 rounded-full bg-destructive text-destructive-foreground"><X className="w-3 h-3 mx-auto" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Sizes</div>
                                <div className="space-y-2">
                                    {editing.sizes.map((s, idx) => (
                                        <div key={idx} className="grid grid-cols-5 gap-2 items-center">
                                            <Input value={s.label} onChange={(e) => {
                                                const sizes = [...editing.sizes]; sizes[idx] = { ...s, label: e.target.value }; setEditing({ ...editing, sizes });
                                            }} placeholder="Label" />
                                            <Input type="number" value={s.price_inr} onChange={(e) => {
                                                const sizes = [...editing.sizes]; sizes[idx] = { ...s, price_inr: Number(e.target.value) }; setEditing({ ...editing, sizes });
                                            }} placeholder="Price ₹" />
                                            <Input type="number" value={s.weight_grams} onChange={(e) => {
                                                const sizes = [...editing.sizes]; sizes[idx] = { ...s, weight_grams: Number(e.target.value) }; setEditing({ ...editing, sizes });
                                            }} placeholder="Weight g" />
                                            <Input type="number" value={s.delivery_days} onChange={(e) => {
                                                const sizes = [...editing.sizes]; sizes[idx] = { ...s, delivery_days: Number(e.target.value) }; setEditing({ ...editing, sizes });
                                            }} placeholder="Delivery days" />
                                            <button onClick={() => setEditing({ ...editing, sizes: editing.sizes.filter((_, i) => i !== idx) })} className="text-destructive"><X className="w-4 h-4" /></button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => setEditing({ ...editing, sizes: [...editing.sizes, { label: "New size", inches: 4, price_inr: 999, weight_grams: 100, delivery_days: 12, active: true }] })} className="text-xs text-primary mt-2">+ Add size</button>
                            </div>

                            <div>
                                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Options (dropdowns shown on product page)</div>
                                <div className="space-y-3">
                                    {(editing.options || []).map((opt, idx) => (
                                        <div key={idx} className="rounded-2xl border p-3 space-y-2" data-testid={`option-row-${idx}`}>
                                            <div className="grid grid-cols-6 gap-2 items-center">
                                                <Input
                                                    className="col-span-3"
                                                    value={opt.name}
                                                    onChange={(e) => {
                                                        const options = [...editing.options];
                                                        options[idx] = { ...opt, name: e.target.value };
                                                        setEditing({ ...editing, options });
                                                    }}
                                                    placeholder="Option name (e.g. Base color)"
                                                    data-testid={`option-name-${idx}`}
                                                />
                                                <label className="col-span-2 flex items-center gap-2 text-xs">
                                                    <input
                                                        type="checkbox"
                                                        checked={opt.required || false}
                                                        onChange={(e) => {
                                                            const options = [...editing.options];
                                                            options[idx] = { ...opt, required: e.target.checked };
                                                            setEditing({ ...editing, options });
                                                        }}
                                                    />
                                                    Required
                                                </label>
                                                <button
                                                    onClick={() => setEditing({ ...editing, options: editing.options.filter((_, i) => i !== idx) })}
                                                    className="text-destructive justify-self-end"
                                                    data-testid={`option-remove-${idx}`}
                                                    type="button"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <Input
                                                value={(opt.values || []).join(", ")}
                                                onChange={(e) => {
                                                    const options = [...editing.options];
                                                    options[idx] = {
                                                        ...opt,
                                                        values: e.target.value.split(",").map((v) => v.trim()).filter(Boolean),
                                                    };
                                                    setEditing({ ...editing, options });
                                                }}
                                                placeholder="Values, comma separated (e.g. Walnut, Marble, Gold)"
                                                data-testid={`option-values-${idx}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setEditing({
                                            ...editing,
                                            options: [...(editing.options || []), { name: "", values: [], required: false }],
                                        })
                                    }
                                    className="text-xs text-primary mt-2"
                                    data-testid="option-add"
                                >
                                    + Add option
                                </button>
                            </div>

                            <div className="flex items-center gap-4 text-sm">
                                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
                                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
                                <label className="flex items-center gap-2" title="Turn off for accessories, Funkos or ready-made items"><input type="checkbox" checked={editing.requires_photos !== false} onChange={(e) => setEditing({ ...editing, requires_photos: e.target.checked })} data-testid="requires-photos-toggle" /> Requires reference photos</label>
                                <label className="flex items-center gap-2" title="Allow customers to pay Cash on Delivery for this product"><input type="checkbox" checked={editing.cod_available === true} onChange={(e) => setEditing({ ...editing, cod_available: e.target.checked })} data-testid="cod-available-toggle" /> COD available</label>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="admin-save-product">Save</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block text-sm">
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        {children}
    </label>
);

export default AdminProducts;
